/**
 * Activity Tracker - Lightweight frontend event batching service
 *
 * Tracks meaningful business events: page views, diamond views, memo requests,
 * admin actions, auth events, errors. NOT trivial UI interactions.
 *
 * Batches up to 10 events, flushes every 5 seconds or on beforeunload.
 */
import { getApiBaseUrl } from '../utils/apiClient';
import { useAuthStore } from '../stores/authStore';
const BATCH_SIZE = 10;
const FLUSH_INTERVAL = 5000; // 5 seconds
const ENDPOINT_PATH = '/activity-log';
class ActivityTrackerService {
    constructor() {
        this.queue = [];
        this.flushTimer = null;
        this.initialized = false;
    }
    /**
     * Initialize the tracker - call once at app startup
     */
    init() {
        if (this.initialized)
            return;
        this.initialized = true;
        // Periodic flush
        this.flushTimer = setInterval(() => this.flush(), FLUSH_INTERVAL);
        // Flush on page unload
        window.addEventListener('beforeunload', () => {
            this.flush(true);
        });
    }
    /**
     * Track a page view (called automatically by route change listener)
     */
    trackPageView(path, title) {
        this.enqueue({
            category: 'user-action',
            action: 'page-view',
            page: path,
            message: `Viewed ${title || path}`,
            metadata: { title: title || document.title }
        });
    }
    /**
     * Track a business action (called manually from components)
     */
    trackAction(action, message, metadata) {
        this.enqueue({
            category: 'user-action',
            action,
            message,
            page: window.location.pathname,
            metadata
        });
    }
    /**
     * Track an admin action
     */
    trackAdminAction(action, message, metadata) {
        this.enqueue({
            category: 'admin',
            action,
            message,
            page: window.location.pathname,
            metadata
        });
    }
    /**
     * Track an error
     */
    trackError(error, context) {
        const msg = error instanceof Error ? error.message : error;
        this.enqueue({
            category: 'error',
            action: 'client-error',
            level: 'error',
            message: msg,
            page: window.location.pathname,
            metadata: {
                ...(error instanceof Error ? { stack: error.stack } : {}),
                ...context
            }
        });
    }
    /**
     * Track an API call (called from apiClient instrumentation)
     */
    trackApiCall(endpoint, method, statusCode, duration) {
        // Skip tracking our own endpoint and logs-query to avoid loops
        if (endpoint.includes('activity-log') || endpoint.includes('logs-query'))
            return;
        this.enqueue({
            category: 'api',
            action: 'api-call',
            message: `${method} ${endpoint} → ${statusCode} (${duration}ms)`,
            metadata: { endpoint, method, statusCode, duration }
        });
    }
    enqueue(event) {
        // Only log for authenticated users
        const { accessToken } = useAuthStore.getState();
        if (!accessToken)
            return;
        this.queue.push({
            ...event,
            timestamp: new Date().toISOString()
        });
        if (this.queue.length >= BATCH_SIZE) {
            this.flush();
        }
    }
    /**
     * Flush queued events to the server
     */
    flush(sync = false) {
        if (this.queue.length === 0)
            return;
        const events = [...this.queue];
        this.queue = [];
        const { accessToken } = useAuthStore.getState();
        const url = `${getApiBaseUrl()}${ENDPOINT_PATH}`;
        const body = JSON.stringify({ events });
        const headers = {
            'Content-Type': 'application/json'
        };
        if (accessToken) {
            headers['Authorization'] = `Bearer ${accessToken}`;
        }
        if (sync && navigator.sendBeacon) {
            // Use sendBeacon for beforeunload (guaranteed delivery)
            const blob = new Blob([body], { type: 'application/json' });
            navigator.sendBeacon(url, blob);
        }
        else {
            // Standard fetch (fire-and-forget)
            fetch(url, { method: 'POST', headers, body }).catch(() => {
                // Silent fail - tracking should never break the app
            });
        }
    }
    /**
     * Destroy the tracker (cleanup)
     */
    destroy() {
        if (this.flushTimer) {
            clearInterval(this.flushTimer);
            this.flushTimer = null;
        }
        this.flush();
        this.initialized = false;
    }
}
// Singleton instance
export const activityTracker = new ActivityTrackerService();
export default activityTracker;
